import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  tableCellClasses
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import { toast } from 'react-toastify';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { fDate, fDateNew } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import SalesToolbar from './SalesToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { isValidValue, normalizeValue, truncateText } from '../../utils/CommonFunction';
import { getMasterListType } from '../../store/Slice/Global/GlobalSlice';
import {
  getMatchedDownloadList,
  getSalesList,
  addRemarkApi,
  viewRemarkApi
} from '../../store/Slice/Sales/SalesSlice';
import useSalesSelector from '../../store/Slice/Sales/SalesSelector';
import useDebounce from '../../utils/useDebounce';

const TABLE_HEAD = [
  { id: 'leadId', label: 'APPLICATION ID', sortable: true },
  { id: 'fileNumber', label: 'REFERENCE NUMBER', sortable: true },
  { id: 'partnerName', label: 'PARTNER', sortable: true },
  { id: 'created', label: 'SUBMISSION DATE', sortable: true },
  { id: 'bankName', label: 'BANK', sortable: true },
  { id: 'city', label: 'CITY', sortable: true },
  { id: 'name', label: 'BORROWER NAME', sortable: true },
  { id: 'loanType', label: 'LOAN TYPE', sortable: true },
  { id: 'monthName', label: 'DISB DATE', sortable: true },
  { id: 'amount2', label: 'DISBURSED AMOUNT', sortable: true },
  { id: 'payoutStatus', label: 'STATUS', sortable: true },
  { id: 'lastUpdated', label: 'LAST UPDATE', sortable: true }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

export default function SalesList() {
  const [selected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [order, setOrder] = useState('desc');
  const [selectStatus, setSelectStatus] = useState(288);
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [filterParameter, setFilterParameter] = useState({
    bankId: null
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);

  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remarkType, setRemarkType] = useState('add'); // 'add' or 'view'
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [loadingRemark, setLoadingRemark] = useState(false);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { SalesListData = [] } = useSalesSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const tableHeaders =
    selectStatus === 260
      ? [...TABLE_HEAD, { id: 'action', label: 'ACTION', sortable: false }]
      : TABLE_HEAD;

  const handleActionClick = async (type, partnerPayoutId) => {
    setRemarkType(type);
    setSelectedPayoutId(partnerPayoutId);
    setRemarkText('');
    setRemarkOpen(true);

    if (type === 'view') {
      setLoadingRemark(true);
      const res = await dispatch(viewRemarkApi(partnerPayoutId));
      if (res && res.success && res.data) {
        setRemarkText(res.data.remarks || 'No remarks found.');
      } else {
        setRemarkText('No remarks found.');
      }
      setLoadingRemark(false);
    }
  };

  const handleRemarkSubmit = async () => {
    if (!remarkText.trim()) {
      toast.error('Please enter a remark.');
      return;
    }
    const payload = {
      partnerPayoutId: selectedPayoutId,
      remarks: remarkText
    };
    const res = await dispatch(addRemarkApi(payload));
    if (res && res.success) {
      toast.success(res?.data?.msg || 'Remark updated successfully');
      setRemarkOpen(false);
      setRemarkText('');
      getPayoutListApi();
    } else {
      toast.error(res?.message || 'Failed to update remark');
    }
  };

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  // Master Type List
  const getMasterTypeListApi = () => {
    const type = 'PAYOUT_STATUS_TYPE';
    dispatch(getMasterListType(type));
  };

  const handleDownloadMatchDetails = async () => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {},
      filterModel: {
        bankId: filterParameter?.bankId || null
      },
      filterPayoutStatusId: isValidValue(selectStatus) ? selectStatus : null
    };

    const res = await dispatch(getMatchedDownloadList(credentials));

    if (!res || !res.success || !res.data) {
      console.error('❌ No CSV data found!');
      return;
    }

    const csvText = String(res.data);

    try {
      const blob = new Blob([`\uFEFF${csvText}`], {
        type: 'text/csv;charset=utf-8;'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Sales${selectStatus === 260 ? 'MisMatched' : 'Matched'}Report.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadPopup(false);
    }
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const getPayoutListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order
      },
      filterModel: {
        bankId: filterParameter?.bankId || null
      },
      filterPayoutStatusId: isValidValue(selectStatus) ? selectStatus : null
    };
    dispatch(getSalesList(credentials));
  }, [
    LoginData,
    rowsPerPage,
    debouncedSearch,
    order,
    selectStatus,
    filterParameter,
    page,
    dispatch
  ]);

  useEffect(() => {
    getPayoutListApi();
  }, [getPayoutListApi]);

  useEffect(() => {
    getMasterTypeListApi();
  }, []);

  useEffect(() => {
    if (!SalesListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(SalesListData.length >= rowsPerPage);
  }, [SalesListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, selectStatus, filterParameter]);

  const totalCount = Array.isArray(SalesListData) ? SalesListData.length : 0;
  const startRecord = totalCount > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = totalCount > 0 ? page * rowsPerPage + totalCount : 0;

  return (
    <Page title="Partner Management System || Sales List">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <PointOfSaleOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Sales List
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Track and manage sales payout records, disbursements, and MIS reports.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Table Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <SalesToolbar
            parent="payout"
            selectStatus={selectStatus}
            setSelectStatus={setSelectStatus}
            handleDownloadMatchDetails={handleDownloadMatchDetails}
            setDownloadPopup={setDownloadPopup}
            downloadPopup={downloadPopup}
            onFilterName={handleFilterByName}
            setFilterParameter={setFilterParameter}
            filterParameter={filterParameter}
            filterName={filterName}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table>
                <thead>
                  <TableRow>
                    {tableHeaders.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                          spacing={0.5}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {SalesListData?.length > 0 &&
                    SalesListData?.map((item, index) => {
                      const isItemSelected = selected.indexOf(item) !== -1;

                      return (
                        <TableRow
                          hover
                          key={index}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          sx={{
                            '&:hover': { backgroundColor: '#f8fafc !important' },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: 'rgba(2, 132, 199, 0.06)'
                            }
                          }}
                        >
                          <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {truncateText(normalizeValue(item?.fileNumber, NOT_AVAILABLE), 15)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 500, color: '#334155' }}>
                            {normalizeValue(item?.partnerName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {isValidValue(item?.fileSubmissionDate)
                              ? fDate(item?.fileSubmissionDate)
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#334155' }}>
                            {normalizeValue(item?.bankName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {normalizeValue(item?.cityName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 500, color: '#334155' }}>
                            {normalizeValue(item?.borrowerName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {normalizeValue(item?.loanTypeName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {normalizeValue(
                              fDateNew(item?.disburseDate || item?.disburseMonth),
                              NOT_AVAILABLE
                            )}
                          </StyledTableCell>
                          <StyledTableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                            {normalizeValue(item?.disburseAmount, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '20px',
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor:
                                  item?.payoutStatus === 'Not Matched' ||
                                  item?.payoutStatus === 'Payment Due'
                                    ? '#fee2e2'
                                    : item?.payoutStatus === 'MIS' ||
                                      item?.payoutStatus === 'Invoice'
                                    ? '#fef3c7'
                                    : '#dcfce7',
                                color:
                                  item?.payoutStatus === 'Not Matched' ||
                                  item?.payoutStatus === 'Payment Due'
                                    ? '#dc2626'
                                    : item?.payoutStatus === 'MIS' ||
                                      item?.payoutStatus === 'Invoice'
                                    ? '#d97706'
                                    : '#16a34a'
                              }}
                            >
                              {item?.payoutStatus ? item?.payoutStatus : NOT_AVAILABLE}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: '#475569' }}>
                            {isValidValue(item?.lastUpdated)
                              ? fDate(item?.lastUpdated)
                              : NOT_AVAILABLE}
                          </StyledTableCell>
                          {selectStatus === 260 && (
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Add Remark" placement="top" arrow>
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: '#0284c7',
                                    bgcolor: 'rgba(2, 132, 199, 0.08)',
                                    borderRadius: '8px',
                                    p: 0.75,
                                    mr: 0.8,
                                    '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.16)' }
                                  }}
                                  onClick={() => handleActionClick('add', item?.partnerPayoutId)}
                                >
                                  <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Remark" placement="top" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: '#16a34a',
                                      bgcolor: 'rgba(22, 163, 74, 0.08)',
                                      borderRadius: '8px',
                                      p: 0.75,
                                      '&:hover': { bgcolor: 'rgba(22, 163, 74, 0.16)' },
                                      '&.Mui-disabled': {
                                        color: '#94a3b8',
                                        bgcolor: '#f1f5f9'
                                      }
                                    }}
                                    onClick={() => handleActionClick('view', item?.partnerPayoutId)}
                                    disabled={!item?.remarks}
                                  >
                                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </StyledTableCell>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
                {(!SalesListData || SalesListData.length === 0) && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        colSpan={tableHeaders.length + 1}
                        sx={{ py: 6 }}
                      >
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Showing{' '}
              <span style={{ fontWeight: 600, color: '#334155' }}>
                {startRecord} to {endRecord}
              </span>{' '}
              records
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={totalCount < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Container>

      {/* Add / View Remark Dialog */}
      <Dialog
        open={remarkOpen}
        onClose={() => setRemarkOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontWeight: 700,
            fontSize: 16,
            color: '#1e293b',
            py: 2,
            px: 3
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
            {remarkType === 'add' ? 'Add Remark' : 'View Remark'}
          </Typography>
          <IconButton onClick={() => setRemarkOpen(false)} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {remarkType === 'add' ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Remark"
              placeholder="Enter your remark here..."
              value={remarkText}
              onChange={(e) => setRemarkText(e.target.value)}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          ) : (
            <Box sx={{ mt: 1 }}>
              {loadingRemark ? (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Loading remark...
                </Typography>
              ) : (
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    minHeight: '100px'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#334155',
                      fontSize: 14
                    }}
                  >
                    {remarkText || 'No remarks found.'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          {remarkType === 'add' ? (
            <>
              <Button
                onClick={() => setRemarkOpen(false)}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  borderColor: '#cbd5e1',
                  color: '#475569',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemarkSubmit}
                variant="contained"
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#0284c7',
                  color: '#ffffff',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0369a1' }
                }}
              >
                Submit
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setRemarkOpen(false)}
              variant="contained"
              sx={{
                borderRadius: '8px',
                bgcolor: '#0284c7',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Page>
  );
}
